import { isNonCustodialWallet } from './linkedWallet.utils';
import { WALLET_CUSTODY_TYPE } from '../../constants/wallet.constants';
import { getCachedOpenOrders } from './openOrdersCache.service';
import {
  readErc20AllowanceRawDirect,
  resolveSpenderAddress,
  compareUintStrings,
} from './nonCustodialWallet.service';
import { logNonCustodialBalance } from './nonCustodialBalance.debug';
import { invalidateReadCache } from './nonCustodialReadRpc';

const OPEN_ORDER_STATUSES = new Set([1, 4]);

const normalizeUintString = (value) =>
  String(value ?? '0').replace(/^0+/, '') || '0';

export const addUintStrings = (leftRaw, rightRaw) => {
  const left = normalizeUintString(leftRaw);
  const right = normalizeUintString(rightRaw);
  let carry = 0;
  let result = '';
  let i = left.length - 1;
  let j = right.length - 1;

  while (i >= 0 || j >= 0 || carry) {
    const sum =
      (i >= 0 ? Number(left[i]) : 0) +
      (j >= 0 ? Number(right[j]) : 0) +
      carry;
    result = String(sum % 10) + result;
    carry = Math.floor(sum / 10);
    i -= 1;
    j -= 1;
  }

  return normalizeUintString(result);
};

export const buildPairFromOrder = (order, fallbackPair = null) => {
  const baseCurrency = String(order?.baseCurrency ?? order?.base_currency ?? '').trim();
  const otherCurrency = String(order?.otherCurrency ?? order?.other_currency ?? '').trim();
  if (baseCurrency && otherCurrency) {
    return {
      baseCurrency,
      otherCurrency,
      pair_key: order?.pairName ?? order?.pair_name ?? order?.pair_key ?? fallbackPair?.pair_key,
    };
  }

  const raw = String(order?.pairName ?? order?.pair_name ?? order?.pair_key ?? '').trim();
  if (!raw) return fallbackPair ?? null;

  if (raw.includes('/')) {
    const [other, base] = raw.split('/');
    if (other && base) {
      return { otherCurrency: other, baseCurrency: base, pair_key: raw };
    }
  }

  const normalized = raw.replace('/', '_');
  const underscoreIndex = normalized.indexOf('_');
  if (underscoreIndex > 0) {
    return {
      otherCurrency: normalized.slice(0, underscoreIndex),
      baseCurrency: normalized.slice(underscoreIndex + 1),
      pair_key: normalized,
    };
  }

  return fallbackPair ?? null;
};

export const getUnfilledOrderQuantity = (order) => {
  const quantity = Number(order?.quantity ?? order?.qty);
  const filled = Number(order?.filled_amount ?? order?.filledAmount ?? 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  const unfilled = quantity - (Number.isFinite(filled) ? filled : 0);
  return unfilled > 0 ? unfilled : 0;
};

export const isOpenOrderStatus = (status) => OPEN_ORDER_STATUSES.has(Number(status));

const isNonCustodialWalletType = (walletType) => {
  const normalized = String(walletType ?? '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === WALLET_CUSTODY_TYPE.NON_CUSTODIAL) return true;
  return !['custodial', 'bitgo', 'c'].includes(normalized);
};

export const orderBelongsToTradeWallet = (order, wallet) => {
  if (!order || !wallet) return false;

  const orderWalletId = String(order?.walletId ?? order?.wallet_id ?? '').trim();
  const tradeWalletId = String(wallet?.walletId ?? wallet?.wallet_id ?? '').trim();
  if (orderWalletId && tradeWalletId) {
    return orderWalletId === tradeWalletId;
  }

  const orderWalletType = order?.walletType ?? order?.wallet_type;
  if (orderWalletType) {
    return isNonCustodialWalletType(orderWalletType) === isNonCustodialWallet(wallet);
  }

  return isNonCustodialWallet(wallet);
};

const buildAllowanceBucketKey = ({ tokenAddress, spenderAddress }) =>
  `${String(tokenAddress ?? '').toLowerCase()}:${String(spenderAddress ?? '').toLowerCase()}`;

export const getOpenOrdersForAllowance = ({ pairName, wallet }) => {
  const resolvedPairName = String(pairName ?? '').trim();
  if (!resolvedPairName) {
    logNonCustodialBalance('getOpenOrdersForAllowance:skipped-empty-pair', {});
    return [];
  }

  return getCachedOpenOrders(resolvedPairName).filter(
    (order) => isOpenOrderStatus(order?.status) && orderBelongsToTradeWallet(order, wallet),
  );
};

export const computeAggregateAllowanceTargets = async ({
  openOrders = [],
  resolveRequirement,
  excludeOrderId = null,
}) => {
  /** @type {Map<string, { tokenAddress: string, spenderAddress: string, assetSymbol: string, requiredAmountRaw: string, wallet: object }>} */
  const buckets = new Map();

  const accumulateRequirement = (requirement) => {
    if (!requirement?.tokenAddress || !requirement?.spenderAddress) return;
    const requiredRaw = normalizeUintString(requirement.requiredAmountRaw);
    if (requiredRaw === '0') return;

    const key = buildAllowanceBucketKey(requirement);
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, {
        tokenAddress: requirement.tokenAddress,
        spenderAddress: requirement.spenderAddress,
        assetSymbol: requirement.assetSymbol,
        requiredAmountRaw: requiredRaw,
        wallet: requirement.wallet,
      });
      return;
    }

    existing.requiredAmountRaw = addUintStrings(
      existing.requiredAmountRaw,
      requiredRaw,
    );
  };

  for (const order of openOrders) {
    if (excludeOrderId != null && Number(order?.id) === Number(excludeOrderId)) {
      continue;
    }

    const requirement = await resolveRequirement(order);
    accumulateRequirement(requirement);
  }

  return buckets;
};

export const reconcileErc20AllowanceDown = async ({
  web3,
  ownerAddress,
  requirement,
  onApprovalTxRequested,
  approveErc20Allowance,
  setNonCustodialRpcBusy,
}) => {
  if (!requirement) return { reconciled: false, skipped: true };

  const targetRaw = normalizeUintString(requirement.requiredAmountRaw);
  const allowanceParams = {
    tokenAddress: requirement.tokenAddress,
    ownerAddress,
    spenderAddress: requirement.spenderAddress,
  };

  setNonCustodialRpcBusy(true);
  try {
    invalidateReadCache();
    const currentAllowanceRaw = await readErc20AllowanceRawDirect(web3, allowanceParams);
    if (compareUintStrings(currentAllowanceRaw, targetRaw) <= 0) {
      logNonCustodialBalance('reconcileErc20AllowanceDown:skipped', {
        assetSymbol: requirement.assetSymbol,
        currentAllowanceRaw,
        targetRaw,
      });
      return { reconciled: false, skipped: true, currentAllowanceRaw, targetRaw };
    }

    logNonCustodialBalance('reconcileErc20AllowanceDown:request', {
      assetSymbol: requirement.assetSymbol,
      currentAllowanceRaw,
      targetRaw,
    });

    const receipt = await approveErc20Allowance(web3, {
      tokenAddress: requirement.tokenAddress,
      ownerAddress,
      spenderAddress: requirement.spenderAddress,
      amountRaw: targetRaw,
      amount: requirement.requiredAmount,
      assetSymbol: requirement.assetSymbol,
      wallet: requirement.wallet,
      onApprovalTxRequested,
      allowZero: true,
    });

    return {
      reconciled: true,
      skipped: false,
      transactionHash: receipt?.transactionHash,
      currentAllowanceRaw: targetRaw,
      targetRaw,
    };
  } finally {
    setNonCustodialRpcBusy(false);
  }
};

export const reconcileAllowanceAfterOrderCancel = async ({
  web3,
  wallet,
  ownerAddress,
  cancelledOrder,
  fallbackPair,
  onApprovalTxRequested,
  approveErc20Allowance,
  setNonCustodialRpcBusy,
  resolveOrderRequirement,
}) => {
  if (!web3 || !wallet || !ownerAddress || !cancelledOrder) {
    return { reconciled: false, skipped: true };
  }

  const spenderAddress = resolveSpenderAddress(wallet, fallbackPair);
  if (!spenderAddress) return { reconciled: false, skipped: true };

  const cancelledExposure = await resolveOrderRequirement(cancelledOrder);
  if (!cancelledExposure) {
    return { reconciled: false, skipped: true };
  }

  const openOrders = getOpenOrdersForAllowance({
    pairName: fallbackPair?.pair_key ?? cancelledOrder?.pairName ?? cancelledOrder?.pair_name,
    wallet,
  });

  const targets = await computeAggregateAllowanceTargets({
    openOrders,
    resolveRequirement: resolveOrderRequirement,
    excludeOrderId: cancelledOrder?.id,
  });

  const bucketKey = buildAllowanceBucketKey(cancelledExposure);
  const aggregate = targets.get(bucketKey);
  const targetRaw = aggregate?.requiredAmountRaw ?? '0';

  return reconcileErc20AllowanceDown({
    web3,
    ownerAddress,
    requirement: {
      ...cancelledExposure,
      requiredAmountRaw: targetRaw,
      requiredAmount: aggregate?.requiredAmount ?? 0,
    },
    onApprovalTxRequested,
    approveErc20Allowance,
    setNonCustodialRpcBusy,
  });
};

export { buildAllowanceBucketKey };
